import type { HTMLAttributes, TableHTMLAttributes, ThHTMLAttributes, TdHTMLAttributes } from 'react'
import { cx } from './cx'
import './table.css'

export interface TableProps extends TableHTMLAttributes<HTMLTableElement> {
  /** Rows navigate somewhere. Adds the pointer cursor the handoff asks for. */
  clickableRows?: boolean
}

export function Table({ clickableRows = false, className, ...rest }: TableProps) {
  return (
    <table className={cx('table', clickableRows && 'table-rows-clickable', className)} {...rest} />
  )
}

export interface ThProps extends ThHTMLAttributes<HTMLTableCellElement> {
  /** Right-aligned tabular figures, for numeric columns. */
  numeric?: boolean
}

export function Th({ numeric = false, className, ...rest }: ThProps) {
  return <th scope="col" className={cx(numeric && 'num', className)} {...rest} />
}

export interface TdProps extends TdHTMLAttributes<HTMLTableCellElement> {
  numeric?: boolean
}

export function Td({ numeric = false, className, ...rest }: TdProps) {
  return <td className={cx(numeric && 'num', className)} {...rest} />
}

export interface RowProps extends HTMLAttributes<HTMLTableRowElement> {
  selected?: boolean
  resolved?: boolean
}

export function Row({ selected, resolved, className, onClick, ...rest }: RowProps) {
  // A row that navigates has to be reachable and operable from the keyboard. Making it
  // focusable and mapping Enter and Space keeps the design's clickable rows accessible
  // without turning every cell into a link.
  const interactive = typeof onClick === 'function'
  return (
    <tr
      className={cx(resolved && 'is-resolved', className)}
      aria-selected={selected}
      tabIndex={interactive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                event.currentTarget.click()
              }
            }
          : undefined
      }
      {...rest}
    />
  )
}
